import { Box, Chip, Stack } from "@mui/material";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { Resources, useResource } from "Translations/Resources";
import { useSearchParams } from "Navigation/useSearchParams";
import { OverviewSection } from "Components/Loyalty/Stardust/OverviewSection";
import { StardustTabs } from "Components/Loyalty/Stardust/StardustTabs";
import { isValidLoyaltyTab } from "Components/Loyalty/Shared/Tabs/config";
import { InfoBox } from "Components/Shared/InfoBox/InfoBox";
import { useIsUserInMooneLite } from "Hooks/useIsUserInMooneLite";
import { MooneLitePopup } from "Components/Dashboard/MooneLitePopup";
import { StardustConsentDialog } from "Components/Loyalty/Stardust/StardustConsentDialog";
import { StardustParticipationSection } from "Components/Loyalty/Stardust/StardustParticipationSection";
import { UserInfoGate } from "Components/Shared/UserInfoGate";
import { useHasStardustConsent } from "Hooks/useHasStardustConsent";

const RESOURCES = Resources.Pages.Content.Stardust;

export const StardustPage = () => {
  const { t } = useResource();

  return (
    <AuthenticatedLayout
      title={t(Resources.Pages.Titles.Stardust)}
      titleBadge={
        <Chip label="Pilot" color="primary" size="small" variant="outlined" />
      }
      backNavigation={{
        mode: "historyStack",
        fallbackUrl: null,
      }}
    >
      <StyledPageWrapper>
        <Box className="left-content" display="flex" flexDirection="column">
          <StardustPageBody />
        </Box>
      </StyledPageWrapper>
    </AuthenticatedLayout>
  );
};

/**
 * Consent first (ZS-1624, legal 2026-09-29): a person who has not consented sees the consent dialog
 * and nothing else, Lite or Full alike. The gate renders nothing until user-info is known, so the
 * content never flashes before the dialog.
 */
const StardustPageBody = () => (
  <UserInfoGate>
    <StardustPageGatedBody />
  </UserInfoGate>
);

/** Rendered only behind the gate, so the consent is already known here. */
const StardustPageGatedBody = () => {
  const { isUserInMooneLite } = useIsUserInMooneLite();
  const { hasStardustConsent } = useHasStardustConsent();

  if (!hasStardustConsent) {
    return <StardustConsentDialog />;
  }

  if (isUserInMooneLite) {
    // Lite users see the completion nag instead of the content, but they DO earn Stardust —
    // so leaving the program must stay reachable for them. Same column as the nag, no extra
    // inset, so the two cards line up.
    return (
      <Stack gap={3}>
        <MooneLitePopup />
        <StardustParticipationSection />
      </Stack>
    );
  }

  return <StardustPageContent />;
};

const StardustPageContent = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useResource();

  const tabParam = searchParams.get("activeTab");
  const activeTab = isValidLoyaltyTab(tabParam) ? tabParam : "challenges";

  return (
    <Box display="flex" flexDirection="column" gap={3} p={3}>
      <InfoBox variant="info" content={t(RESOURCES.PilotDisclaimer)} />
      <OverviewSection />
      <StardustTabs
        initialTab={activeTab}
        onTabChange={(id) => setSearchParams({ activeTab: id })}
      />
      <StardustParticipationSection />
    </Box>
  );
};

export default StardustPage;
